import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageCircle, Send, Megaphone, Users, Search, RefreshCw, Loader2,
  Check, CheckCheck, Clock, AlertCircle, ChevronLeft, ChevronRight,
  Filter, User, Play, Pause, Eye, X, ArrowUpRight, ArrowDownLeft,
  Image, Trash2, Upload, Link2, ExternalLink, FileCheck, Camera,
  Settings, Bot
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import api from '../lib/api';
import { toast } from 'sonner';

// ─── IMAGE UPLOADER ──────────────────────────────────────────────────────────
const ImageUploader = ({ token, imageUrl, onImageChange, onClear }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlMode, setUrlMode] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef(null);

  const uploadFile = async (file) => {
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Formato no válido. Use JPG, PNG, WebP o GIF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('Máximo 5MB'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/admin/upload/image?authorization=Bearer ${token}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.ok) { onImageChange(res.data.url); toast.success('Imagen subida'); }
    } catch (err) { toast.error('Error al subir imagen'); }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      <Label className="text-[10px] text-[#F7F2E7]/50 flex items-center gap-1"><Image size={10} /> Imagen</Label>
      {!imageUrl ? (
        <>
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) uploadFile(f); }}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-white/[0.12] hover:border-white/25 bg-[#0E1016]'}`}
          >
            <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => { if (e.target.files[0]) uploadFile(e.target.files[0]); }} className="hidden" />
            {uploading ? (
              <div className="flex flex-col items-center gap-2"><Loader2 size={24} className="animate-spin text-blue-400" /><p className="text-xs text-[#F7F2E7]/60">Subiendo...</p></div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload size={20} className="text-[#F7F2E7]/40" />
                <p className="text-sm text-[#F7F2E7]/80">Arrastra o haz clic</p>
                <p className="text-[9px] text-[#F7F2E7]/30">JPG, PNG, WebP · Máx 5MB</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-white/[0.08]" />
            <button onClick={() => setUrlMode(!urlMode)} className="text-[10px] text-[#F7F2E7]/40 hover:text-[#F7F2E7]/60">{urlMode ? 'Cancelar' : 'O usar URL'}</button>
            <div className="flex-1 h-px bg-white/[0.08]" />
          </div>
          {urlMode && (
            <div className="flex gap-2">
              <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://..." className="bg-[#0E1016] border-white/[0.12] text-[#F7F2E7] h-8 text-xs flex-1" />
              <Button onClick={() => { if (urlInput.trim()) { onImageChange(urlInput.trim()); setUrlInput(''); setUrlMode(false); } }} size="sm" className="h-8 bg-blue-600 hover:bg-blue-700">Usar</Button>
            </div>
          )}
        </>
      ) : (
        <div className="relative bg-[#0E1016] rounded-lg border border-blue-500/20 p-3">
          <div className="flex items-start gap-3">
            <img src={imageUrl} alt="Preview" className="w-24 h-24 rounded-lg object-cover border border-white/[0.08]" onError={() => onClear()} />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[#F7F2E7]/80 truncate">Imagen seleccionada</p>
              <p className="text-[9px] text-[#F7F2E7]/40 truncate mt-1">{imageUrl}</p>
              <Button onClick={onClear} size="sm" variant="outline" className="mt-2 h-6 px-2 text-[10px] border-red-500/30 text-red-400 hover:bg-red-500/10">
                <Trash2 size={10} className="mr-1" /> Quitar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const AdminMessaging = ({ token }) => {
  const [activeTab, setActiveTab] = useState('conversations');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get(`/admin/messaging/telegram/status?authorization=Bearer ${token}`);
      setStatus(res.data);
    } catch (err) {
      console.error('Error fetching status:', err);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const tabs = [
    { id: 'conversations', label: 'Conversaciones', icon: MessageCircle },
    { id: 'send', label: 'Enviar', icon: Send },
    { id: 'bulk', label: 'Masivo', icon: Megaphone },
    { id: 'proofs', label: 'Comprobantes', icon: FileCheck },
  ];

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-[#FFC640]" /></div>;
  }

  const botOk = status?.bot?.ok;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-heading font-bold text-[#F7F2E7] flex items-center gap-2">
            <Send className="text-blue-400" size={22} />
            Mensajería Telegram
          </h2>
          <p className="text-xs text-[#F7F2E7]/50 mt-1">
            {status?.users?.linked || 0} usuarios vinculados · {status?.messaging?.conversations || 0} conversaciones
            {(status?.proofs?.pending || 0) > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px]">
                {status.proofs.pending} comprobante{status.proofs.pending > 1 ? 's' : ''} pendiente{status.proofs.pending > 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Setup Webhook Button */}
          <Button
            variant="outline"
            size="sm"
            data-testid="tg-setup-webhook-btn"
            onClick={async () => {
              try {
                const res = await api.post(`/admin/messaging/telegram/setup?authorization=Bearer ${token}`, {});
                if (res.data.ok) toast.success('Webhook registrado correctamente');
                else toast.error(res.data.message || 'Error al registrar webhook');
                fetchStatus();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Error al configurar webhook');
              }
            }}
            className="border-white/[0.12] text-[#F7F2E7]/50 hover:bg-white/5 text-xs"
          >
            <Settings size={12} className="mr-1" /> Registrar Webhook
          </Button>

          {/* Bot Status */}
          <div data-testid="tg-bot-status" className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-2 ${botOk ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
            <div className={`w-1.5 h-1.5 rounded-full ${botOk ? 'bg-blue-500' : 'bg-red-500'}`} />
            {botOk ? `@${status?.bot?.username || 'conectado'}` : 'Bot sin configurar'}
          </div>
        </div>
      </div>

      {/* No config warning */}
      {!botOk && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-300">
          <strong>Telegram no configurado.</strong> Agrega <code className="bg-black/30 px-1 rounded text-xs">TELEGRAM_BOT_TOKEN</code> y <code className="bg-black/30 px-1 rounded text-xs">TELEGRAM_BOT_USERNAME</code> en las variables de entorno (Secrets), luego haz clic en <strong>"Registrar Webhook"</strong>.
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/[0.08]">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            data-testid={`tg-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap
              ${activeTab === t.id ? 'border-blue-500 text-blue-400' : 'border-transparent text-[#F7F2E7]/50 hover:text-[#F7F2E7]/80'}`}
          >
            <t.icon size={14} />
            {t.label}
            {t.id === 'proofs' && (status?.proofs?.pending || 0) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[9px]">{status.proofs.pending}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'conversations' && <ConversationsTab token={token} />}
      {activeTab === 'send' && <SendTab token={token} />}
      {activeTab === 'bulk' && <BulkTab token={token} />}
      {activeTab === 'proofs' && <ProofsTab token={token} onUpdate={fetchStatus} />}
    </div>
  );
};

// ─── CONVERSATIONS TAB ───────────────────────────────────────────────────────
const ConversationsTab = ({ token }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (search) params.q = search;
      const res = await api.get('/admin/messaging/telegram/conversations', {
        params: { ...params, authorization: `Bearer ${token}` }
      });
      setConversations(res.data.data || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) { toast.error('Error al cargar conversaciones'); }
    setLoading(false);
  }, [token, page, search]);

  const fetchMessages = useCallback(async (convId) => {
    setMessagesLoading(true);
    try {
      const res = await api.get(`/admin/messaging/telegram/conversations/${convId}/messages`, {
        params: { limit: 60, authorization: `Bearer ${token}` }
      });
      setMessages(res.data.data || []);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) { toast.error('Error al cargar mensajes'); }
    setMessagesLoading(false);
  }, [token]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);
  useEffect(() => { if (selectedConv?.id) fetchMessages(selectedConv.id); }, [selectedConv, fetchMessages]);

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedConv) return;
    setSending(true);
    try {
      const payload = { text: replyText };
      if (selectedConv.user_id) payload.user_id = selectedConv.user_id;
      else payload.chat_id = selectedConv.chat_id;

      const res = await api.post(`/admin/messaging/telegram/send?authorization=Bearer ${token}`, payload);
      if (res.data.ok) {
        toast.success('Mensaje enviado');
        setReplyText('');
        fetchMessages(selectedConv.id);
      } else {
        toast.error(res.data.error || 'Error al enviar');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar'); }
    setSending(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40" />
            <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Buscar..." className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] pl-9 h-9 text-xs" />
          </div>
          <Button onClick={fetchConversations} variant="outline" size="sm" data-testid="tg-conv-refresh" className="bg-[#121521] border-white/[0.12] h-9 w-9 p-0">
            <RefreshCw size={14} />
          </Button>
        </div>

        <Card className="bg-[#121521] border border-white/[0.08] max-h-[500px] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center"><Loader2 size={20} className="animate-spin text-blue-400 mx-auto" /></div>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-center text-[#F7F2E7]/40 text-sm">No hay conversaciones</div>
          ) : (
            <div className="divide-y divide-white/[0.06]">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  data-testid={`tg-conv-item-${conv.id}`}
                  onClick={() => setSelectedConv(conv)}
                  className={`w-full p-3 text-left hover:bg-white/[0.03] transition-colors ${selectedConv?.id === conv.id ? 'bg-blue-500/5 border-l-2 border-blue-500' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <User size={14} className="text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#F7F2E7] truncate">{conv.user?.name || conv.chat_id}</p>
                      <p className="text-[10px] text-[#F7F2E7]/40 truncate">
                        {conv.user?.telegram_username ? `@${conv.user.telegram_username}` : conv.chat_id}
                      </p>
                    </div>
                  </div>
                  <p className="text-[10px] text-[#F7F2E7]/30 mt-1">
                    {conv.last_message_at ? new Date(conv.last_message_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                  </p>
                </button>
              ))}
            </div>
          )}
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} variant="outline" size="sm" className="h-7 w-7 p-0"><ChevronLeft size={14} /></Button>
            <span className="text-xs text-[#F7F2E7]/50">{page}/{totalPages}</span>
            <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} variant="outline" size="sm" className="h-7 w-7 p-0"><ChevronRight size={14} /></Button>
          </div>
        )}
      </div>

      {/* Chat Panel */}
      <div className="lg:col-span-2">
        <Card className="bg-[#121521] border border-white/[0.08] h-[520px] flex flex-col">
          {!selectedConv ? (
            <div className="flex-1 flex items-center justify-center text-[#F7F2E7]/30">
              <div className="text-center"><MessageCircle size={40} className="mx-auto mb-2 opacity-50" /><p className="text-sm">Selecciona una conversación</p></div>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-white/[0.08] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <User size={14} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#F7F2E7]">{selectedConv.user?.name || 'Usuario'}</p>
                    <p className="text-[10px] text-[#F7F2E7]/50">
                      {selectedConv.user?.telegram_username ? `@${selectedConv.user.telegram_username}` : selectedConv.chat_id}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedConv(null)} className="text-[#F7F2E7]/40 hover:text-[#F7F2E7]">
                  <X size={16} />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {messagesLoading ? (
                  <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-blue-400" /></div>
                ) : messages.length === 0 ? (
                  <p className="text-center text-[#F7F2E7]/30 text-sm py-10">Sin mensajes</p>
                ) : (
                  messages.map((msg, idx) => (
                    <div key={msg.id || idx} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-xl px-3 py-2 ${msg.direction === 'outbound' ? 'bg-blue-600/20 text-[#F7F2E7]' : 'bg-[#0E1016] text-[#F7F2E7]'}`}>
                        {msg.type === 'photo' && msg.media_url ? (
                          <img src={msg.media_url} alt="foto" className="max-w-full rounded-lg max-h-40 mb-1" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : null}
                        {msg.text && <p className="text-sm break-words whitespace-pre-wrap">{msg.text}</p>}
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <span className="text-[9px] text-[#F7F2E7]/30">
                            {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                          {msg.direction === 'outbound' && <Check size={12} className="text-blue-400" />}
                          {msg.direction === 'inbound' && <ArrowDownLeft size={12} className="text-[#F7F2E7]/30" />}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSendReply} className="p-3 border-t border-white/[0.08] flex gap-2">
                <Input
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Escribe un mensaje..."
                  className="bg-[#0E1016] border-white/[0.12] text-[#F7F2E7] flex-1 h-9 text-sm"
                  disabled={sending}
                  data-testid="tg-reply-input"
                />
                <Button type="submit" disabled={sending || !replyText.trim()} className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4" data-testid="tg-reply-send-btn">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </Button>
              </form>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

// ─── SEND TAB ─────────────────────────────────────────────────────────────────
const SendTab = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [showUserList, setShowUserList] = useState(false);

  const searchUsers = useCallback(async (q) => {
    if (!q || q.length < 2) { setUsers([]); return; }
    setLoading(true);
    try {
      const res = await api.get('/admin/messaging/telegram/users', {
        params: { q, limit: 20, authorization: `Bearer ${token}` }
      });
      setUsers(res.data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    const timer = setTimeout(() => { if (search && !selectedUser) { searchUsers(search); setShowUserList(true); } }, 300);
    return () => clearTimeout(timer);
  }, [search, selectedUser, searchUsers]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!selectedUser || !messageText.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/admin/messaging/telegram/send?authorization=Bearer ${token}`, {
        user_id: selectedUser.user_id, text: messageText
      });

      if (res.data.ok) { toast.success('Mensaje enviado por Telegram'); setMessageText(''); }
      else toast.error(res.data.error || 'Error al enviar');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar mensaje'); }
    setSending(false);
  };

  return (
    <Card className="bg-[#121521] border border-white/[0.08]">
      <CardHeader>
        <CardTitle className="text-base font-heading text-blue-400 flex items-center gap-2">
          <Send size={16} /> Enviar Mensaje Individual
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs text-[#F7F2E7]/60">Seleccionar usuario</Label>
          <div className="relative">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); if (selectedUser) setSelectedUser(null); }}
                onFocus={() => users.length > 0 && setShowUserList(true)}
                placeholder="Buscar por nombre, email o @username..."
                className="bg-[#0E1016] border-white/[0.12] text-[#F7F2E7] pl-9 pr-8 h-10"
                data-testid="tg-send-user-search"
              />
              {selectedUser && (
                <button type="button" onClick={() => { setSelectedUser(null); setSearch(''); setUsers([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40 hover:text-[#F7F2E7]">
                  <X size={14} />
                </button>
              )}
            </div>

            {showUserList && users.length > 0 && !selectedUser && (
              <div className="absolute z-10 w-full mt-1 bg-[#0E1016] border border-white/[0.12] rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {users.map((user) => (
                  <button key={user.user_id} onClick={() => { setSelectedUser(user); setSearch(user.name || user.phone); setShowUserList(false); }} className="w-full p-3 text-left hover:bg-white/[0.05] border-b border-white/[0.06] last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <User size={14} className="text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#F7F2E7] truncate">{user.name}</p>
                        <p className="text-[10px] text-[#F7F2E7]/50 truncate">
                          {user.telegram_username ? `@${user.telegram_username} · ` : ''}{user.email}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {loading && <div className="absolute right-10 top-1/2 -translate-y-1/2"><Loader2 size={14} className="animate-spin text-blue-400" /></div>}
          </div>
        </div>

        {selectedUser && (
          <div className="bg-[#0E1016] rounded-xl p-4 border border-blue-500/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <User size={18} className="text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[#F7F2E7]">{selectedUser.name}</p>
                <p className="text-xs text-[#F7F2E7]/50">{selectedUser.email}</p>
              </div>
              <div className="text-right">
                {selectedUser.telegram_username && <p className="text-sm text-blue-400 font-mono">@{selectedUser.telegram_username}</p>}
                <p className="text-[10px] text-[#F7F2E7]/30">{selectedUser.telegram_chat_id}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-xs text-[#F7F2E7]/60">Mensaje</Label>
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Escribe tu mensaje aquí..."
            rows={4}
            className="w-full bg-[#0E1016] border border-white/[0.12] text-[#F7F2E7] rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm"
            disabled={!selectedUser}
            data-testid="tg-send-message-input"
          />
        </div>

        <Button onClick={handleSend} disabled={sending || !selectedUser || !messageText.trim()} className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto" data-testid="tg-send-submit-btn">
          {sending ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
          Enviar por Telegram
        </Button>
      </CardContent>
    </Card>
  );
};

// ─── BULK TAB ─────────────────────────────────────────────────────────────────
const BulkTab = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const [campaignName, setCampaignName] = useState('');
  const [campaignMessage, setCampaignMessage] = useState('');
  const [campaignImageUrl, setCampaignImageUrl] = useState('');
  const [messageType, setMessageType] = useState('text');
  const [creating, setCreating] = useState(false);

  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('users');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/messaging/telegram/users', {
        params: { page, limit: 100, q: search, authorization: `Bearer ${token}` }
      });
      setUsers(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) { toast.error('Error al cargar usuarios'); }
    setLoading(false);
  }, [token, page, search]);

  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const res = await api.get('/admin/messaging/telegram/campaigns', {
        params: { limit: 25, authorization: `Bearer ${token}` }
      });
      setCampaigns(res.data.data || []);
    } catch (err) { console.error(err); }
    setCampaignsLoading(false);
  }, [token]);

  useEffect(() => {
    if (viewMode === 'users') fetchUsers();
    else fetchCampaigns();
  }, [viewMode, fetchUsers, fetchCampaigns]);

  const handleSelectUser = (userId) => {
    const s = new Set(selectedUsers);
    s.has(userId) ? s.delete(userId) : s.add(userId);
    setSelectedUsers(s);
    setSelectAll(false);
  };

  const handleSelectAllPage = () => {
    if (selectAll) { setSelectedUsers(new Set()); setSelectAll(false); }
    else { const s = new Set(selectedUsers); users.forEach(u => s.add(u.user_id)); setSelectedUsers(s); setSelectAll(true); }
  };

  const handleCreateCampaign = async () => {
    if (!campaignName.trim()) { toast.error('Nombre de campaña requerido'); return; }
    if (messageType === 'text' && !campaignMessage.trim()) { toast.error('Mensaje requerido'); return; }
    if (messageType === 'photo' && !campaignImageUrl.trim()) { toast.error('Imagen requerida'); return; }
    if (selectedUsers.size === 0) { toast.error('Selecciona al menos un usuario'); return; }

    setCreating(true);
    try {
      const payload = {
        name: campaignName,
        message_type: messageType,
        selected_user_ids: Array.from(selectedUsers),
        text: campaignMessage || null,
        image_url: messageType === 'photo' ? campaignImageUrl : null
      };
      const res = await api.post(`/admin/messaging/telegram/campaigns?authorization=Bearer ${token}`, payload);
      if (res.data.ok) {
        toast.success(`Campaña creada: ${res.data.total_recipients} destinatarios`);
        const sendRes = await api.post(`/admin/messaging/telegram/campaigns/${res.data.campaign_id}/start?authorization=Bearer ${token}`, {});
        if (sendRes.data.ok) toast.success('Campaña iniciada');
        setCampaignName(''); setCampaignMessage(''); setCampaignImageUrl(''); setMessageType('text');
        setSelectedUsers(new Set()); setSelectAll(false); setViewMode('campaigns'); fetchCampaigns();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear campaña'); }
    setCreating(false);
  };

  const statusBadge = (status) => ({
    draft: 'bg-gray-500/10 text-gray-400', sending: 'bg-blue-500/10 text-blue-400',
    paused: 'bg-amber-500/10 text-amber-400', completed: 'bg-green-500/10 text-green-400',
    failed: 'bg-red-500/10 text-red-400'
  }[status] || 'bg-gray-500/10 text-gray-400');

  const templates = [
    { name: 'Nuevo Sorteo', text: '🎉 ¡NUEVO SORTEO EN COFRES MÁGICOS!\n\n📦 Cofres disponibles\n💰 Grandes premios\n🎁 99 cofres GRATIS\n\n¡Participa ahora en cofresmagicos.com!' },
    { name: 'Recordatorio', text: '⏰ ¡No olvides completar tu compra!\n\nTu cofre está esperando.\n\nVisita cofresmagicos.com para finalizar.' },
    { name: 'Ganador', text: '🏆 ¡TENEMOS GANADOR!\n\nEl sorteo ha finalizado.\n\nVisita cofresmagicos.com para ver los resultados.\n\n¡Gracias por participar!' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant={viewMode === 'users' ? 'default' : 'outline'} onClick={() => setViewMode('users')} className={viewMode === 'users' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-[#121521] border-white/[0.12] text-[#F7F2E7]/60'} size="sm" data-testid="tg-bulk-users-btn">
          <Users size={14} className="mr-1" /> Seleccionar ({selectedUsers.size})
        </Button>
        <Button variant={viewMode === 'campaigns' ? 'default' : 'outline'} onClick={() => setViewMode('campaigns')} className={viewMode === 'campaigns' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-[#121521] border-white/[0.12] text-[#F7F2E7]/60'} size="sm" data-testid="tg-bulk-campaigns-btn">
          <Megaphone size={14} className="mr-1" /> Historial
        </Button>
      </div>

      {viewMode === 'users' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Users Table */}
          <Card className="bg-[#121521] border border-white/[0.08]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading text-[#F7F2E7]">Usuarios Telegram ({total})</CardTitle>
                <div className="flex gap-2">
                  <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Buscar..." className="bg-[#0E1016] border-white/[0.12] text-[#F7F2E7] h-7 w-40 text-xs" data-testid="tg-bulk-search" />
                  <Button onClick={fetchUsers} variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#F7F2E7]/40"><RefreshCw size={12} /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[350px] overflow-y-auto">
                {loading ? (
                  <div className="p-6 text-center"><Loader2 size={20} className="animate-spin text-blue-400 mx-auto" /></div>
                ) : users.length === 0 ? (
                  <div className="p-6 text-center text-[#F7F2E7]/40 text-sm">
                    <p>No hay usuarios con Telegram vinculado</p>
                    <p className="text-[10px] mt-1">Los usuarios deben conectar Telegram desde su panel</p>
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-[#0E1016] sticky top-0">
                      <tr>
                        <th className="p-2 text-left"><input type="checkbox" checked={selectAll} onChange={handleSelectAllPage} className="rounded border-white/20" data-testid="tg-bulk-select-all" /></th>
                        <th className="p-2 text-left text-[#F7F2E7]/50">Nombre</th>
                        <th className="p-2 text-left text-[#F7F2E7]/50">Telegram</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04]">
                      {users.map((user) => (
                        <tr key={user.user_id} className="hover:bg-white/[0.02]">
                          <td className="p-2"><input type="checkbox" checked={selectedUsers.has(user.user_id)} onChange={() => handleSelectUser(user.user_id)} className="rounded border-white/20" /></td>
                          <td className="p-2 text-[#F7F2E7]">{user.name}</td>
                          <td className="p-2 text-blue-400/80 font-mono text-[10px]">{user.telegram_username ? `@${user.telegram_username}` : user.telegram_chat_id?.slice(-6)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="p-2 border-t border-white/[0.06] flex items-center justify-between">
                <span className="text-[10px] text-[#F7F2E7]/40">Pág {page}/{totalPages} · {selectedUsers.size} seleccionados</span>
                <div className="flex gap-1">
                  <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} variant="ghost" size="sm" className="h-6 w-6 p-0"><ChevronLeft size={12} /></Button>
                  <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} variant="ghost" size="sm" className="h-6 w-6 p-0"><ChevronRight size={12} /></Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Campaign Form */}
          <Card className="bg-[#121521] border border-blue-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading text-blue-400 flex items-center gap-2"><Megaphone size={14} /> Crear Campaña</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-[10px] text-[#F7F2E7]/50 mb-1.5 block">Plantillas rápidas</Label>
                <div className="flex flex-wrap gap-1">
                  {templates.map((t, i) => (
                    <Button key={i} variant="outline" size="sm" onClick={() => { setCampaignMessage(t.text); if (!campaignName) setCampaignName(t.name); }} className="text-[10px] h-6 px-2 border-white/[0.12] hover:bg-white/5">{t.name}</Button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[10px] text-[#F7F2E7]/50 mb-1 block">Nombre de campaña</Label>
                <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="Ej: Sorteo Navidad 2026" className="bg-[#0E1016] border-white/[0.12] text-[#F7F2E7] h-8 text-xs" data-testid="tg-campaign-name-input" />
              </div>

              <div>
                <Label className="text-[10px] text-[#F7F2E7]/50 mb-1.5 block">Tipo</Label>
                <div className="flex gap-1">
                  {[['text', 'Solo Texto', MessageCircle], ['photo', 'Con Imagen', Camera]].map(([val, label, Icon]) => (
                    <Button key={val} type="button" variant={messageType === val ? 'default' : 'outline'} size="sm" onClick={() => setMessageType(val)} className={`text-[10px] h-7 ${messageType === val ? 'bg-blue-600 hover:bg-blue-700' : 'bg-[#0E1016] border-white/[0.12] text-[#F7F2E7]/60'}`}>
                      <Icon size={12} className="mr-1" />{label}
                    </Button>
                  ))}
                </div>
              </div>

              {messageType === 'photo' && (
                <ImageUploader token={token} imageUrl={campaignImageUrl} onImageChange={setCampaignImageUrl} onClear={() => setCampaignImageUrl('')} />
              )}

              <div>
                <Label className="text-[10px] text-[#F7F2E7]/50 mb-1 block">{messageType === 'photo' ? 'Caption (opcional)' : 'Mensaje'}</Label>
                <textarea value={campaignMessage} onChange={(e) => setCampaignMessage(e.target.value)} placeholder={messageType === 'photo' ? 'Texto que acompaña la imagen...' : 'Mensaje a enviar...'} rows={messageType === 'photo' ? 3 : 5} className="w-full bg-[#0E1016] border border-white/[0.12] text-[#F7F2E7] rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-xs" data-testid="tg-campaign-message-input" />
              </div>

              <div className="bg-[#0E1016] rounded-lg p-3 flex items-center justify-between">
                <div><p className="text-[10px] text-[#F7F2E7]/40">Destinatarios</p><p className="text-lg font-heading font-bold text-blue-400">{selectedUsers.size}</p></div>
                <Button onClick={handleCreateCampaign} disabled={creating || selectedUsers.size === 0 || !campaignName || (messageType === 'text' ? !campaignMessage : !campaignImageUrl)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="tg-campaign-create-btn">
                  {creating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Play size={14} className="mr-2" />}
                  Crear y Enviar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="bg-[#121521] border border-white/[0.08]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-heading text-[#F7F2E7]">Historial de Campañas</CardTitle>
              <Button onClick={fetchCampaigns} variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#F7F2E7]/40"><RefreshCw size={12} /></Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {campaignsLoading ? (
              <div className="p-6 text-center"><Loader2 size={20} className="animate-spin text-blue-400 mx-auto" /></div>
            ) : campaigns.length === 0 ? (
              <div className="p-6 text-center text-[#F7F2E7]/40 text-sm">No hay campañas</div>
            ) : (
              <div className="divide-y divide-white/[0.06]">
                {campaigns.map((campaign) => (
                  <div key={campaign.id} className="p-3 hover:bg-white/[0.02]" data-testid={`tg-campaign-item-${campaign.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium text-[#F7F2E7]">{campaign.name}</h4>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusBadge(campaign.status)}`}>{campaign.status?.toUpperCase()}</span>
                      </div>
                      <div className="flex gap-1">
                        {campaign.status === 'sending' && (
                          <Button onClick={async () => { await api.post(`/admin/messaging/telegram/campaigns/${campaign.id}/pause?authorization=Bearer ${token}`, {}); fetchCampaigns(); }} size="sm" variant="outline" className="h-6 px-2 border-amber-500/50 text-amber-400 text-[10px]">
                            <Pause size={10} className="mr-1" /> Pausar
                          </Button>
                        )}
                        {['draft', 'paused'].includes(campaign.status) && (
                          <Button onClick={async () => { await api.post(`/admin/messaging/telegram/campaigns/${campaign.id}/start?authorization=Bearer ${token}`, {}); fetchCampaigns(); }} size="sm" className="h-6 px-2 bg-blue-600 hover:bg-blue-700 text-[10px]">
                            <Play size={10} className="mr-1" /> {campaign.status === 'paused' ? 'Reanudar' : 'Enviar'}
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-4 text-[10px] text-[#F7F2E7]/50">
                      <span>Total: <span className="text-[#F7F2E7]">{campaign.total_targets}</span></span>
                      <span>Enviados: <span className="text-blue-400">{campaign.sent_count || 0}</span></span>
                      <span>Fallidos: <span className="text-red-400">{campaign.failed_count || 0}</span></span>
                      <span>{campaign.created_at ? new Date(campaign.created_at).toLocaleDateString('es-MX') : ''}</span>
                    </div>
                    {campaign.message_type === 'photo' && campaign.image_url && (
                      <div className="mt-2 flex items-start gap-2 bg-[#0E1016] rounded p-2">
                        <img src={campaign.image_url} alt="Campaign" className="w-16 h-16 rounded object-cover" onError={(e) => e.target.style.display = 'none'} />
                        {campaign.text && <p className="text-[11px] text-[#F7F2E7]/60 line-clamp-3">{campaign.text}</p>}
                      </div>
                    )}
                    {campaign.message_type !== 'photo' && campaign.text && (
                      <p className="mt-2 text-[11px] text-[#F7F2E7]/60 bg-[#0E1016] rounded p-2 line-clamp-2">{campaign.text}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── PROOFS TAB ───────────────────────────────────────────────────────────────
const ProofsTab = ({ token, onUpdate }) => {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [processing, setProcessing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('received');

  const fetchProofs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/messaging/telegram/proofs', {
        params: { page, limit: 20, status: filterStatus, authorization: `Bearer ${token}` }
      });
      setProofs(res.data.data || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) { toast.error('Error al cargar comprobantes'); }
    setLoading(false);
  }, [token, page, filterStatus]);

  useEffect(() => { fetchProofs(); }, [fetchProofs]);

  const handleApprove = async (proofId) => {
    setProcessing(proofId);
    try {
      await api.post(`/admin/messaging/telegram/proofs/${proofId}/approve?authorization=Bearer ${token}`, {});
      toast.success('Pago confirmado y notificación enviada por Telegram');
      fetchProofs();
      if (onUpdate) onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al confirmar'); }
    setProcessing(null);
  };

  const handleReject = async (proofId) => {
    const reason = prompt('Motivo del rechazo (opcional):') || 'Comprobante no válido';
    setProcessing(proofId);
    try {
      await api.post(`/admin/messaging/telegram/proofs/${proofId}/reject?authorization=Bearer ${token}`, { reason });
      toast.success('Comprobante rechazado');
      fetchProofs();
      if (onUpdate) onUpdate();
    } catch (err) { toast.error('Error al rechazar'); }
    setProcessing(null);
  };

  const statusLabels = {
    received: { label: 'Pendientes', color: 'bg-amber-500/10 text-amber-400' },
    approved: { label: 'Aprobados', color: 'bg-green-500/10 text-green-400' },
    rejected: { label: 'Rechazados', color: 'bg-red-500/10 text-red-400' }
  };

  return (
    <Card className="bg-[#121521] border border-white/[0.08]">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base font-heading text-[#F7F2E7] flex items-center gap-2">
            <Camera size={16} className="text-blue-400" /> Comprobantes de Pago (Telegram)
          </CardTitle>
          <div className="flex items-center gap-2">
            {Object.entries(statusLabels).map(([val, { label, color }]) => (
              <button key={val} onClick={() => { setFilterStatus(val); setPage(1); }} className={`text-[10px] px-3 py-1 rounded-full transition-all ${filterStatus === val ? color + ' ring-1 ring-white/20' : 'bg-white/5 text-[#F7F2E7]/40 hover:bg-white/10'}`} data-testid={`tg-proofs-filter-${val}`}>
                {label}
              </button>
            ))}
            <Button onClick={fetchProofs} variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#F7F2E7]/40"><RefreshCw size={12} /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center"><Loader2 size={20} className="animate-spin text-blue-400 mx-auto" /></div>
        ) : proofs.length === 0 ? (
          <div className="py-10 text-center text-[#F7F2E7]/40">
            <Camera size={40} className="mx-auto mb-2 opacity-50" />
            <p>No hay comprobantes {statusLabels[filterStatus]?.label?.toLowerCase()}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {proofs.map((proof) => (
              <div key={proof.id} className="bg-[#0E1016] rounded-xl p-4 border border-white/[0.06]" data-testid={`tg-proof-item-${proof.id}`}>
                <div className="flex items-start gap-4">
                  {/* Image preview */}
                  {proof.media_url && (
                    <a href={proof.media_url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                      <img src={proof.media_url} alt="Comprobante" className="w-20 h-20 rounded-lg object-cover border border-white/[0.08] hover:opacity-80 transition-opacity" onError={(e) => e.target.style.display = 'none'} />
                    </a>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {proof.purchase && (
                        <span className="font-mono text-sm text-[#FFC640]">#{proof.purchase.purchase_id?.substring(0, 8).toUpperCase()}</span>
                      )}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusLabels[proof.status]?.color || 'bg-gray-500/10 text-gray-400'}`}>
                        {statusLabels[proof.status]?.label || proof.status}
                      </span>
                    </div>
                    <p className="text-sm text-[#F7F2E7]">{proof.user?.name || 'Usuario desconocido'}</p>
                    {proof.user?.telegram_username && <p className="text-[10px] text-blue-400">@{proof.user.telegram_username}</p>}
                    {proof.purchase && (
                      <div className="flex gap-3 mt-2 text-xs text-[#F7F2E7]/50">
                        <span>Cofres: <span className="text-[#F7F2E7]">{proof.purchase.chest_count}</span></span>
                        <span>Total: <span className="text-[#FFC640] font-bold">${proof.purchase.total?.toFixed(2)}</span></span>
                      </div>
                    )}
                    <p className="text-[10px] text-[#F7F2E7]/30 mt-1">{proof.created_at ? new Date(proof.created_at).toLocaleString('es-MX') : ''}</p>
                  </div>
                  {proof.status === 'received' && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button onClick={() => handleApprove(proof.id)} disabled={processing === proof.id} size="sm" className="h-7 px-3 bg-green-600 hover:bg-green-700 text-white text-[10px]" data-testid={`tg-proof-approve-${proof.id}`}>
                        {processing === proof.id ? <Loader2 size={10} className="animate-spin" /> : <><Check size={10} className="mr-1" /> Aprobar</>}
                      </Button>
                      <Button onClick={() => handleReject(proof.id)} disabled={processing === proof.id} variant="outline" size="sm" className="h-7 px-3 border-red-500/50 text-red-400 text-[10px] hover:bg-red-500/10" data-testid={`tg-proof-reject-${proof.id}`}>
                        <X size={10} className="mr-1" /> Rechazar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-white/[0.06]">
            <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} variant="ghost" size="sm" className="h-7 w-7 p-0"><ChevronLeft size={14} /></Button>
            <span className="text-xs text-[#F7F2E7]/50">{page}/{totalPages}</span>
            <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} variant="ghost" size="sm" className="h-7 w-7 p-0"><ChevronRight size={14} /></Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminMessaging;
